import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';

type CallPopupAction = 'answer' | 'decline';

interface PopupOwnership {
  owner: string;
  heartbeat: number;
}

@Injectable({
  providedIn: 'root',
})
export class CallPopupService {
  private readonly channel =
    typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('connect-call-popup')
      : null;
  private readonly tabId = `tab-${Math.random().toString(36).slice(2)}`;
  private readonly ownerKey = 'connect-call-popup-owner';
  private readonly beatKey = 'connect-call-popup-heartbeat';
  private heartbeatTimer: any;
  private isOwner = false;
  private popupWindow: Window | null = null;
  private popupPrimed = false;
  private latestCallData: any = null;

  private actionSubject = new Subject<CallPopupAction>();
  action$: Observable<CallPopupAction> = this.actionSubject.asObservable();

  constructor(private zone: NgZone) {
    this.bootstrapOwnership();
    this.channel?.addEventListener('message', (event) =>
      this.zone.run(() => this.handleMessage(event.data))
    );
    window.addEventListener('storage', (event) => {
      if (event.key === this.ownerKey || event.key === this.beatKey) {
        this.ensureOwnership();
      }
    });
  }

  announceIncomingCall(callData: any): void {
    if (!callData || !this.channel) return;
    this.latestCallData = callData;
    this.channel.postMessage({ type: 'incoming-call', callData });
    if (this.isOwner) {
      this.openPopup(callData);
    }
  }

  updateCallStatus(status: string): void {
    if (!this.channel) return;
    this.channel.postMessage({ type: 'call-status', status });
  }

  notifyCallEnded(): void {
    if (!this.channel) return;
    this.channel.postMessage({ type: 'call-ended' });
    this.closePopup();
  }

  private handleMessage(data: any): void {
    if (!data?.type) return;

    switch (data.type) {
      case 'incoming-call':
        if (this.isOwner) {
          this.openPopup(data.callData);
        }
        break;
      case 'popup-ready':
        if (this.isOwner && this.latestCallData && this.channel) {
          this.channel.postMessage({
            type: 'call-data',
            callData: this.latestCallData,
          });
        }
        break;
      case 'popup-action':
        if (!this.isOwner) return;
        if (data.action === 'answer' || data.action === 'decline') {
          this.actionSubject.next(data.action);
        }
        break;
      case 'close-popup':
        this.closePopup();
        break;
    }
  }

  private openPopup(callData: any): void {
    if (!this.channel) return;
    this.latestCallData = callData;
    this.ensureOwnership();
    if (!this.isOwner) return;

    const { url, features } = this.getPopupWindowConfig();
    if (!this.popupWindow || this.popupWindow.closed) {
      this.popupWindow = window.open(url, 'connect-call-popup', features);
    } else {
      try {
        this.popupWindow.location.href = url;
      } catch {
        // If navigation fails, fallback to reopening the window.
        this.popupWindow = window.open(url, 'connect-call-popup', features);
      }
      this.popupWindow.focus();
    }

    this.channel.postMessage({ type: 'call-data', callData });
  }

  /**
   * Pre-open the popup window on a user gesture so browsers don't block it when a call arrives.
   */
  primePopupWindow(): boolean {
    this.ensureOwnership();
    if (!this.isOwner) return false;
    if (this.popupWindow && !this.popupWindow.closed) return true;
    if (this.popupPrimed) return false;

    const { features } = this.getPopupWindowConfig();
    const primed = window.open('', 'connect-call-popup', features);
    if (primed) {
      this.popupWindow = primed;
      this.popupPrimed = true;
      primed.blur();
      window.focus();
      return true;
    }

    this.popupPrimed = false;
    return false;
  }

  private closePopup(): void {
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.close();
    }
    this.popupWindow = null;
    this.latestCallData = null;
    this.popupPrimed = false;
  }

  private bootstrapOwnership(): void {
    this.ensureOwnership(true);
    this.heartbeatTimer = window.setInterval(
      () => this.ensureOwnership(),
      4000
    );
  }

  private ensureOwnership(force = false): void {
    const owner = localStorage.getItem(this.ownerKey);
    const beat = Number(localStorage.getItem(this.beatKey) || 0);
    const stale = !beat || Date.now() - beat > 12000;

    if (force && !owner) {
      this.claimOwnership();
      return;
    }

    if (!owner || stale) {
      this.claimOwnership();
      return;
    }

    this.isOwner = owner === this.tabId;
    if (this.isOwner) {
      localStorage.setItem(this.beatKey, Date.now().toString());
    }
  }

  private claimOwnership(): void {
    const snapshot: PopupOwnership = {
      owner: this.tabId,
      heartbeat: Date.now(),
    };
    localStorage.setItem(this.ownerKey, snapshot.owner);
    localStorage.setItem(this.beatKey, snapshot.heartbeat.toString());
    this.isOwner = true;
  }

  private getPopupWindowConfig(): { url: string; features: string } {
    const width = 360;
    const height = 420;
    const left =
      (window.screen?.availWidth || window.innerWidth || 1200) - width - 24;
    const top = 48;
    const url = `${window.location.origin}/call-popup`;
    const features = [
      'popup=yes',
      'toolbar=no',
      'location=no',
      'status=no',
      'menubar=no',
      'scrollbars=no',
      'resizable=no',
      'titlebar=no',
      'alwaysRaised=yes',
      'alwaysOnTop=yes',
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
    ].join(',');

    return { url, features };
  }
}
